import React from "react";
import { Button, Flex, ModalV2, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import { fileIconType, type EffectiveDoc } from "#utils/classificationGrouping";

export type RemoveDocumentConfirmModalProps = {
  isOpen: boolean;
  doc: EffectiveDoc | null;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
};

/**
 * RemoveDocumentConfirmModal — confirms removing a single document from the
 * ingestion. The backend honors a never-delete contract; this is a soft-delete
 * from the user's perspective.
 */
export const RemoveDocumentConfirmModal = ({
  isOpen,
  doc,
  onClose,
  onConfirm,
  loading,
}: RemoveDocumentConfirmModalProps) => {
  if (!doc) {
    return null;
  }

  return (
    // Every dismissal path closes while the remove write is in flight, not just
    // the Cancel button — otherwise escape or the close button strands the user
    // outside a destructive action they can't tell succeeded.
    <ModalV2
      show={isOpen}
      onHide={onClose}
      closeOnClickOutside={!loading}
      escapeToClose={!loading}
      closeButton={!loading}
      size="small"
      title="Remove document"
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={12}>
          <Flex alignItems="center" gap={Sizes.SM}>
            <Icon type={fileIconType(doc.filename)} variant="solid" aria-hidden="true" />
            <Text asSpan lines={1}>
              {doc.filename}
            </Text>
          </Flex>
          <Text variant="regular-md">
            This will remove the document from this batch. This action cannot be undone.
          </Text>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={Sizes.SM}>
          <Button variant="neutral" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} isLoading={loading}>
            Remove
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
